// Product-page guide finder: a few questions → the blog guides (and the
// exact sections in them) that fit the visitor's room. Content and scoring
// live in src/lib/product-guides.ts.

import { Fragment, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen, RotateCcw } from "lucide-react";
import { useLocale } from "@/lib/i18n";
import { LocaleLink } from "@/components/LocaleLink";
import { getPostBySlug, headingId } from "@/lib/blog-posts";
import { getBlogTheme } from "@/lib/blog-themes";
import {
  activeSteps,
  recommendGuides,
  type GuideOption,
  type ProductGuideConfig,
} from "@/lib/product-guides";
import { trackProductGuideCompleted } from "@/lib/analytics";

/** Renders "Find den rette *guide*" with the starred part in terracotta italic. */
function Emphasis({ text }: { text: string }) {
  return (
    <>
      {text.split("*").map((part, i) =>
        i % 2 === 1 ? (
          <em key={i} className="text-terracotta">
            {part}
          </em>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

const fade = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const },
};

export function ProductGuide({ config }: { config: ProductGuideConfig }) {
  const locale = useLocale();
  const da = locale === "da";
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const sectionRef = useRef<HTMLElement>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = activeSteps(config, answers);
  const done = stepIndex >= steps.length;
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const results = useMemo(
    () => (done ? recommendGuides(config, answers) : []),
    [done, config, answers],
  );

  const choose = (option: GuideOption) => {
    const next = { ...answers, [step.id]: option.id };
    // Drop answers to steps that no longer apply (e.g. table shape after
    // switching away from "dining table").
    const stillActive = new Set(activeSteps(config, next).map((s) => s.id));
    for (const k of Object.keys(next)) if (!stillActive.has(k)) delete next[k];
    setAnswers(next);
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    if (nextIndex >= activeSteps(config, next).length) {
      // Results are taller than a step; bring the top of the section back
      // into view if the visitor has scrolled past it.
      const el = sectionRef.current;
      if (el && el.getBoundingClientRect().top < 0) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      trackProductGuideCompleted({
        guide_id: config.id,
        answers: next,
        guides: recommendGuides(config, next).map((r) => r.post),
      });
    }
  };

  const restart = () => {
    setAnswers({});
    setStepIndex(0);
  };

  return (
    <section ref={sectionRef} className="scroll-mt-28 pb-20 md:pb-28">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <div className="rounded-sm border border-border bg-cream/60 px-5 py-10 md:px-12 md:py-14">
          <div className="max-w-2xl">
            <p className="eyebrow">{config.eyebrow[locale]}</p>
            <h2 className="mt-4 font-display text-[clamp(1.9rem,4vw,3rem)] leading-[1.02] tracking-tight">
              <Emphasis
                text={
                  done
                    ? da
                      ? "Her er dine *guides*"
                      : "Here are your *guides*"
                    : config.title[locale]
                }
              />
            </h2>
            <p className="mt-4 max-w-xl text-sm text-muted-foreground md:text-base">
              {done
                ? da
                  ? "Ud fra dine svar er det disse afsnit, der giver mest mening at læse."
                  : "Based on your answers, these are the sections most worth reading."
                : config.intro[locale]}
            </p>
          </div>

          {!done && (
            <div className="mt-8 flex items-center gap-5">
              <p className="shrink-0 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                {da ? "Trin" : "Step"} {stepIndex + 1} / {steps.length}
              </p>
              <div className="flex flex-1 items-center gap-1.5" aria-hidden>
                {steps.map((s, i) => (
                  <span
                    key={s.id}
                    className={`h-px flex-1 transition-colors ${i <= stepIndex ? "bg-foreground" : "bg-border"}`}
                  />
                ))}
              </div>
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={() => setStepIndex((i) => i - 1)}
                  className="inline-flex shrink-0 items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-terracotta"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {da ? "Tilbage" : "Back"}
                </button>
              )}
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            {!done ? (
              <motion.div key={step.id} {...fade} className="mt-8">
                <h3 className="font-display text-xl leading-tight md:text-2xl">
                  <Emphasis text={step.question[locale]} />
                </h3>
                <div
                  className={`mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 ${
                    step.options.length > 3
                      ? "lg:grid-cols-4"
                      : step.options.length === 3
                        ? "lg:grid-cols-3"
                        : ""
                  }`}
                >
                  {step.options.map((o) => (
                    <OptionCard
                      key={o.id}
                      option={o}
                      locale={locale}
                      selected={answers[step.id] === o.id}
                      onClick={() => choose(o)}
                    />
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div key="results" {...fade} className="mt-10">
                <div className="grid gap-6 md:grid-cols-2 md:gap-8">
                  {results.map((r, i) => (
                    <GuideResult key={r.post} rec={r} locale={locale} primary={i === 0} />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={restart}
                  className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-terracotta"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {da ? "Svar igen" : "Start over"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function OptionCard({
  option,
  locale,
  selected,
  onClick,
}: {
  option: GuideOption;
  locale: "da" | "en";
  selected: boolean;
  onClick: () => void;
}) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`group flex items-center gap-4 rounded-sm border bg-card px-4 py-4 text-left transition-all sm:min-h-[132px] sm:flex-col sm:items-start sm:justify-between sm:gap-6 sm:p-5 duration-300 hover:-translate-y-1 hover:border-foreground hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${
        selected ? "border-foreground" : "border-border"
      }`}
    >
      <span className="flex shrink-0 items-center gap-1 text-terracotta" aria-hidden>
        {Array.from({ length: option.iconCount ?? 1 }, (_, i) => (
          <Icon key={i} className="h-5 w-5" strokeWidth={1.5} />
        ))}
      </span>
      <span>
        <span className="block font-display text-base leading-tight text-foreground transition-colors group-hover:text-terracotta md:text-lg">
          {option.label[locale]}
        </span>
        {option.hint && (
          <span className="mt-1.5 block text-xs text-muted-foreground md:text-[13px]">
            {option.hint[locale]}
          </span>
        )}
      </span>
    </button>
  );
}

function GuideResult({
  rec,
  locale,
  primary,
}: {
  rec: ReturnType<typeof recommendGuides>[number];
  locale: "da" | "en";
  primary: boolean;
}) {
  const post = getPostBySlug(rec.post);
  if (!post) return null;
  const c = post[locale];
  const theme = getBlogTheme(post.theme);
  const da = locale === "da";
  const params = { slug: post.theme, post: post.slug };

  return (
    <article className="flex flex-col overflow-hidden rounded-sm border border-border bg-card">
      <LocaleLink to="/blog/$slug/$post" params={params} className="group block">
        <div className="aspect-[16/9] overflow-hidden bg-muted">
          <img
            src={post.cover}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          />
        </div>
        <div className="px-5 pt-5 md:px-6">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {primary && <span className="text-terracotta">{da ? "Start her" : "Start here"}</span>}
            {primary && theme && <span aria-hidden>·</span>}
            {theme && <span>{theme[locale].label}</span>}
            <span aria-hidden>·</span>
            <span>
              {post.readingMinutes} {da ? "min." : "min"}
            </span>
          </div>
          <h3 className="mt-3 font-display text-xl leading-tight text-foreground transition-colors group-hover:text-terracotta md:text-2xl">
            {c.title}
          </h3>
        </div>
      </LocaleLink>

      <div className="flex flex-1 flex-col px-5 pb-5 pt-4 md:px-6 md:pb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {da ? "Læs især" : "Especially read"}
        </p>
        <ul className="mt-2 divide-y divide-border border-y border-border">
          {rec.sections.map((s) => (
            <li key={s.heading[locale]}>
              <LocaleLink
                to="/blog/$slug/$post"
                params={params}
                hash={headingId(s.heading[locale])}
                className="group flex items-center justify-between gap-4 py-3 text-sm text-foreground/80 hover:text-terracotta"
              >
                <span>{s.heading[locale]}</span>
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </LocaleLink>
            </li>
          ))}
        </ul>
        <LocaleLink
          to="/blog/$slug/$post"
          params={params}
          className="mt-5 inline-flex items-center gap-2 self-start text-[11px] uppercase tracking-[0.22em] text-terracotta hover:text-foreground"
        >
          <BookOpen className="h-3.5 w-3.5" />
          {da ? "Læs hele guiden" : "Read the full guide"}
        </LocaleLink>
      </div>
    </article>
  );
}
